import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileUploadDropzone } from "@/components/ui/file-upload-dropzone";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Check, X, ArrowRight, Download } from "lucide-react";

// Note: Sensitive fields (tax_id, social_security_number, iban, bic, health_insurance_*)
// are now encrypted and stored separately. CSV import only handles non-sensitive fields.
const EMPLOYEE_FIELDS = [
  { key: "name", label: "Name (kombiniert)", required: false },
  { key: "first_name", label: "Vorname", required: false },
  { key: "last_name", label: "Nachname", required: false },
  { key: "position", label: "Position", required: true },
  { key: "employee_number", label: "Personalnummer", required: false },
  { key: "department", label: "Abteilung", required: false },
  { key: "private_email", label: "E-Mail", required: false },
  { key: "phone", label: "Telefon", required: false },
  { key: "mobile", label: "Mobil", required: false },
  { key: "birth_date", label: "Geburtsdatum", required: false },
  { key: "gender", label: "Geschlecht", required: false },
  { key: "nationality", label: "Nationalität", required: false },
  { key: "address_street", label: "Straße", required: false },
  { key: "address_zip", label: "PLZ", required: false },
  { key: "address_city", label: "Stadt", required: false },
  { key: "address_country", label: "Land", required: false },
  { key: "employment_start_date", label: "Eintrittsdatum", required: false },
  { key: "contract_type", label: "Vertragsart", required: false },
  { key: "weekly_hours", label: "Wochenstunden", required: false },
  { key: "vacation_days", label: "Urlaubstage", required: false },
  { key: "bank_name", label: "Bank", required: false },
];

export function EmployeeCSVImport() {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState<"upload" | "mapping" | "preview" | "done">("upload");
  const [importResults, setImportResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });

  const parseCSV = (text: string): string[][] => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    return lines.map(line => {
      const values: string[] = [];
      let current = "";
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if ((char === "," || char === ";") && !inQuotes) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    });
  };

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith(".csv")) {
      toast.error("Bitte wählen Sie eine CSV-Datei aus");
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      
      if (parsed.length < 2) {
        toast.error("Die CSV-Datei muss mindestens eine Kopfzeile und eine Datenzeile enthalten");
        return;
      }

      setCsvHeaders(parsed[0]);
      setCsvData(parsed.slice(1));
      setColumnMapping({});
      setStep("mapping");
    };
    reader.readAsText(file);
  };

  const downloadCSVTemplate = () => {
    const headers = EMPLOYEE_FIELDS.map(f => f.label).join(";");
    const exampleRow = [
      "Max Mustermann",
      "Max",
      "Mustermann",
      "Projektleiter",
      "EMP001",
      "IT",
      "max@example.com",
      "030-123456",
      "0170-1234567",
      "01.01.1990",
      "männlich",
      "deutsch",
      "Musterstraße 1",
      "12345",
      "Berlin",
      "Deutschland",
      "01.01.2020",
      "Vollzeit",
      "40",
      "30",
      "12345678901",
      "12 010190 M 001",
      "Deutsche Bank",
      "DE89370400440532013000",
      "COBADEFFXXX",
    ].join(";");

    const csvContent = `${headers}\n${exampleRow}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "mitarbeiter_vorlage.csv";
    link.click();
  };

  const handleMappingChange = (csvColumn: string, employeeField: string) => {
    setColumnMapping(prev => {
      const newMapping = { ...prev };
      // Remove any existing mapping to this employee field
      Object.keys(newMapping).forEach(key => {
        if (newMapping[key] === employeeField) {
          delete newMapping[key];
        }
      });
      if (employeeField && employeeField !== "ignore") {
        newMapping[csvColumn] = employeeField;
      }
      return newMapping;
    });
  };

  const getMappedData = () => {
    return csvData.map(row => {
      const employee: Record<string, any> = {};
      csvHeaders.forEach((header, index) => {
        const fieldKey = columnMapping[header];
        if (fieldKey && row[index]) {
          let value: any = row[index];
          
          // Convert dates
          if (fieldKey.includes("date") && value) {
            // Try to parse German date format (DD.MM.YYYY)
            const germanDate = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
            if (germanDate) {
              value = `${germanDate[3]}-${germanDate[2].padStart(2, "0")}-${germanDate[1].padStart(2, "0")}`;
            }
          }
          
          // Convert numbers
          if ((fieldKey === "weekly_hours" || fieldKey === "vacation_days") && value) {
            value = parseFloat(value.replace(",", "."));
          }
          
          employee[fieldKey] = value;
        }
      });
      return employee;
    });
  };

  const canProceedToPreview = () => {
    const mappedFields = Object.values(columnMapping);
    // Need position and either "name" OR both "first_name" and "last_name"
    const hasPosition = mappedFields.includes("position");
    const hasName = mappedFields.includes("name");
    const hasFirstAndLast = mappedFields.includes("first_name") && mappedFields.includes("last_name");
    return hasPosition && (hasName || hasFirstAndLast);
  };

  const handleImport = async () => {
    if (!user) return;
    
    setImporting(true);
    const mappedData = getMappedData();
    let success = 0;
    let failed = 0;

    for (const employee of mappedData) {
      // Build name from first_name + last_name if not provided directly
      let finalName = employee.name as string;
      if (!finalName && employee.first_name && employee.last_name) {
        finalName = `${employee.first_name} ${employee.last_name}`;
      }
      
      if (!finalName || !employee.position) {
        failed++;
        continue;
      }

      try {
        const insertData = {
          name: finalName,
          position: employee.position as string,
          employee_number: employee.employee_number || null,
          department: employee.department || null,
          private_email: employee.private_email || null,
          phone: employee.phone || null,
          mobile: employee.mobile || null,
          birth_date: employee.birth_date || null,
          gender: employee.gender || null,
          nationality: employee.nationality || null,
          address_street: employee.address_street || null,
          address_zip: employee.address_zip || null,
          address_city: employee.address_city || null,
          address_country: employee.address_country || null,
          employment_start_date: employee.employment_start_date || null,
          contract_type: employee.contract_type || null,
          weekly_hours: employee.weekly_hours || null,
          vacation_days: employee.vacation_days || null,
          bank_name: employee.bank_name || null,
          created_by: user.id,
        };
        
        const { error } = await supabase.from("employees").insert(insertData);

        if (error) {
          console.error("Error importing employee:", error);
          failed++;
        } else {
          success++;
        }
      } catch (error) {
        console.error("Error importing employee:", error);
        failed++;
      }
    }

    setImportResults({ success, failed });
    setStep("done");
    setImporting(false);

    if (success > 0) {
      toast.success(`${success} Mitarbeiter erfolgreich importiert`);
    }
    if (failed > 0) {
      toast.error(`${failed} Mitarbeiter konnten nicht importiert werden`);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setColumnMapping({});
    setStep("upload");
    setImportResults({ success: 0, failed: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Mitarbeiter CSV Import
        </CardTitle>
        <CardDescription>
          Importieren Sie Mitarbeiterdaten aus einer CSV-Datei
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {step === "upload" && (
          <div className="space-y-4">
            <FileUploadDropzone
              accept=".csv"
              onFileSelect={handleFileSelect}
              selectedFile={selectedFile}
              onClear={() => setSelectedFile(null)}
              title="CSV-Datei hochladen"
              description="Ziehen Sie eine CSV-Datei hierher oder klicken Sie zum Auswählen. Die erste Zeile sollte die Spaltenüberschriften enthalten."
              acceptedFormats={["CSV"]}
              supportedFields={EMPLOYEE_FIELDS.map(f => ({ label: f.label, required: f.required }))}
            />
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={downloadCSVTemplate}>
                <Download className="mr-2 h-4 w-4" />
                CSV-Vorlage herunterladen
              </Button>
            </div>
          </div>
        )}

        {step === "mapping" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Ordnen Sie die CSV-Spalten den Mitarbeiterfeldern zu
              </p>
              <Button variant="outline" size="sm" onClick={resetImport}>
                Zurück
              </Button>
            </div>
            
            <ScrollArea className="h-[400px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CSV-Spalte</TableHead>
                    <TableHead></TableHead>
                    <TableHead>Mitarbeiterfeld</TableHead>
                    <TableHead>Beispieldaten</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvHeaders.map((header, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{header}</TableCell>
                      <TableCell>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={columnMapping[header] || ""}
                          onValueChange={(value) => handleMappingChange(header, value)}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Feld auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ignore">-- Ignorieren --</SelectItem>
                            {EMPLOYEE_FIELDS.map(field => (
                              <SelectItem 
                                key={field.key} 
                                value={field.key}
                                disabled={Object.values(columnMapping).includes(field.key) && columnMapping[header] !== field.key}
                              >
                                {field.label}{field.required && " *"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {csvData[0]?.[index] || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            <div className="flex items-center justify-between">
              <p className="text-sm">
                {csvData.length} Datensätze gefunden
              </p>
              <Button 
                onClick={() => setStep("preview")}
                disabled={!canProceedToPreview()}
              >
                Vorschau anzeigen
              </Button>
            </div>
            {!canProceedToPreview() && (
              <p className="text-sm text-destructive">
                Bitte ordnen Sie mindestens die Pflichtfelder zu: Position und entweder "Name (kombiniert)" ODER "Vorname" + "Nachname".
              </p>
            )}
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Überprüfen Sie die Daten vor dem Import
              </p>
              <Button variant="outline" size="sm" onClick={() => setStep("mapping")}>
                Zurück zur Zuordnung
              </Button>
            </div>

            <ScrollArea className="h-[400px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    {EMPLOYEE_FIELDS.filter(f => Object.values(columnMapping).includes(f.key)).map(field => (
                      <TableHead key={field.key}>{field.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getMappedData().slice(0, 50).map((employee, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      {EMPLOYEE_FIELDS.filter(f => Object.values(columnMapping).includes(f.key)).map(field => (
                        <TableCell key={field.key} className="max-w-[150px] truncate">
                          {employee[field.key] || "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {csvData.length > 50 && (
              <p className="text-sm text-muted-foreground">
                Zeige erste 50 von {csvData.length} Datensätzen
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetImport}>
                Abbrechen
              </Button>
              <Button onClick={handleImport} disabled={importing}>
                {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {csvData.length} Mitarbeiter importieren
              </Button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="text-center py-8 space-y-4">
            <div className="flex justify-center gap-8">
              <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-green-600">{importResults.success}</p>
                <p className="text-sm text-muted-foreground">Erfolgreich</p>
              </div>
              {importResults.failed > 0 && (
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
                    <X className="h-8 w-8 text-red-600" />
                  </div>
                  <p className="text-2xl font-bold text-red-600">{importResults.failed}</p>
                  <p className="text-sm text-muted-foreground">Fehlgeschlagen</p>
                </div>
              )}
            </div>
            <Button onClick={resetImport}>
              Weiteren Import starten
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
